import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import useInvalidateQueryOnUnmount from 'hooks/useInvalidateQueryOnUnmount';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import {
  ColdLeadAggregationResult,
  DataRoomDashboardTimeframe,
} from 'router/subrouters/DataRoom/models/Dashboard';
import { PaginatedData } from 'models/PaginatedData';
import {
  getInfiniteQueryItems,
  getInfiniteQueryTotalItems,
  getNextPageParam,
} from 'queryUtils';

const COLD_LEADS_QUERY_KEY = 'data-room-cold-leads';
const LIMIT = '10';

type UseColdLeadsInfiniteQueryProps = {
  dataRoomId?: string;
  timeframe?: DataRoomDashboardTimeframe;
};

export default function useColdLeadsInfiniteQuery({
  dataRoomId,
  timeframe,
}: UseColdLeadsInfiniteQueryProps) {
  const query = useInfiniteQuery<PaginatedData<ColdLeadAggregationResult>>({
    queryKey: [COLD_LEADS_QUERY_KEY, { dataRoomId, timeframe }],
    queryFn: async ({ pageParam = 1 }) => {
      const { data } = await dataRoomApi.room.getColdLeads(dataRoomId, {
        $page: pageParam.toString(),
        $limit: LIMIT,
        timeframe,
      });

      return data;
    },
    enabled: !!dataRoomId && !!timeframe,
    keepPreviousData: true,
    getNextPageParam,
  });

  const items = useMemo(() => getInfiniteQueryItems(query.data), [query.data]);

  const totalItems = getInfiniteQueryTotalItems(query.data);

  useInvalidateQueryOnUnmount(COLD_LEADS_QUERY_KEY);

  return { query, items, totalItems };
}
